import styled from '@emotion/styled';
import React, { useRef } from 'react';
import { BlueButton, Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { DateTime } from 'luxon';
import { useButton, useClickOutside } from '~Hooks';
import { GetBookings_operativeViewer_bookingsForVenueAndDate } from '~Providers/BookingsProvider/api/__queries__';
import { BookingData } from '@baemingo/table-events/dist/types';
import useI18n from '~Translations';
import { formatPhoneNumber } from '../utils';

const Wrapper = styled.div`
  background-color: var(--colorBlueGray1100);
  padding: 16px;
  overflow-y: auto;
  width: 364px;
  border-left: 1px solid var(--colorBlueGray1000);
`;

const InfoSection = styled(Box)`
  background-color: var(--colorBlueGray1200);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Badge = styled.span`
  background-color: rgba(118, 75, 162, 0.3);
  color: var(--colorWhite);
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
`;

const Comment = styled.span`
  display: inline-block;
  border-radius: 4px;
  background-color: transparent;
  border: 1px solid var(--colorBlueGray200);
  font-size: 12px;
  color: var(--colorBlueGray200);
  padding: 8px 12px;
  margin-top: 12px;
  max-width: 100%;
  font-weight: 600;
`;

interface Props {
  chequeName?: string;
  tableName: string;
  tableId?: string;
  partySize: number;
  startTime: string;
  booking?: GetBookings_operativeViewer_bookingsForVenueAndDate;
  chequeBookingData?: BookingData | null;
  onClose: () => void;
  onNavigateToTable: (tableId: string) => void;
}

const ChequeDetails: React.FC<Props> = ({
  chequeName,
  tableName,
  tableId,
  partySize,
  startTime,
  booking,
  chequeBookingData,
  onClose,
  onNavigateToTable,
}) => {
  const { i18n } = useI18n();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Use click outside hook to close the details panel
  useClickOutside(wrapperRef, onClose, true, ['[data-booking-id]', '[data-cheque-block]']);

  const goToTableButton = useButton({
    onClick: () => {
      if (tableId) {
        onNavigateToTable(tableId);
      }
    },
    disabled: !tableId,
  });

  return (
    <Wrapper ref={wrapperRef}>
      <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="medium">
        <Heading level={3} color="var(--colorWhite)">
          {i18n('Bookings.ChequeDetails')}
        </Heading>
        {(booking || chequeBookingData) && <Badge>{i18n('Bookings.FromBooking')}</Badge>}
        {!booking && !chequeBookingData && <Badge>{i18n('Bookings.WalkIn')}</Badge>}
      </Box>

      <InfoSection>
        <InfoRow>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('Bookings.ChequeName')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorWhite)">
            {chequeName}
          </Paragraph>
        </InfoRow>
        <InfoRow>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('Bookings.Table')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorWhite)">
            {tableName}
          </Paragraph>
        </InfoRow>
        <InfoRow>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('Bookings.PartySize')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorWhite)">
            {partySize} {i18n('Bookings.Guests')}
          </Paragraph>
        </InfoRow>
        <InfoRow>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('Bookings.SeatedAt')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorWhite)">
            {DateTime.fromISO(startTime).toFormat('HH:mm')}
          </Paragraph>
        </InfoRow>
      </InfoSection>

      {booking && (
        <InfoSection>
          <Heading level={4} bottom={12} color="var(--colorWhite)">
            {i18n('Bookings.BookingInformation')}
          </Heading>
          <InfoRow>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('Bookings.GuestName')}
            </Paragraph>
            <Paragraph level={3} color="var(--colorWhite)">
              {booking.guestName || i18n('Bookings.DefaultValue')}
            </Paragraph>
          </InfoRow>
          <InfoRow>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('Bookings.Phone')}
            </Paragraph>
            <Paragraph level={3} color="var(--colorWhite)">
              {formatPhoneNumber(booking.guestPhone) || i18n('Bookings.DefaultValue')}
            </Paragraph>
          </InfoRow>
          <InfoRow>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('Bookings.Email')}
            </Paragraph>
            <Paragraph level={3} color="var(--colorWhite)">
              {booking.guestEmail || i18n('Bookings.DefaultValue')}
            </Paragraph>
          </InfoRow>
          <InfoRow>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('Bookings.ReservedTime')}
            </Paragraph>
            <Paragraph level={3} color="var(--colorWhite)">
              {booking.startTime} - {booking.endTime}
            </Paragraph>
          </InfoRow>
          {booking.comment && <Comment>{booking.comment}</Comment>}
        </InfoSection>
      )}

      {/* Fallback: show booking info from cheque.bookingData when no backend booking exists */}
      {!booking && chequeBookingData && (
        <InfoSection>
          <Heading level={4} bottom={12} color="var(--colorWhite)">
            {i18n('Bookings.BookingInformation')}
          </Heading>
          <InfoRow>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('Bookings.GuestName')}
            </Paragraph>
            <Paragraph level={3} color="var(--colorWhite)">
              {[chequeBookingData.firstName, chequeBookingData.lastName].filter(Boolean).join(' ').trim() ||
                i18n('Bookings.DefaultValue')}
            </Paragraph>
          </InfoRow>
          {chequeBookingData.numberOfGuests > 0 && (
            <InfoRow>
              <Paragraph level={3} color="var(--colorBlueGray200)">
                {i18n('Bookings.PartySize')}
              </Paragraph>
              <Paragraph level={3} color="var(--colorWhite)">
                {chequeBookingData.numberOfGuests} {i18n('Bookings.Guests')}
              </Paragraph>
            </InfoRow>
          )}
          {chequeBookingData.comment && <Comment>{chequeBookingData.comment}</Comment>}
        </InfoSection>
      )}

      <BlueButton fluid {...goToTableButton}>
        {i18n('Bookings.GoToTableLowercase')}
      </BlueButton>
    </Wrapper>
  );
};

export default ChequeDetails;
